import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useSessionContext } from '../context/SessionContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Label from '../components/ui/Label';
import Textarea from '../components/ui/Textarea';
import { FiPlus, FiX, FiArrowLeft, FiSave, FiChevronDown, FiSearch } from 'react-icons/fi';

const SessionEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getSessionById, updateSession } = useSessionContext();
  const [session, setSession] = useState(null);
  const [techniques, setTechniques] = useState([]);
  const [newTechnique, setNewTechnique] = useState({
    name: '',
    outcome: 'Attempted'
  });
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  useEffect(() => {
    const data = getSessionById(id);
    if (data) {
      setSession(data);
      setTechniques(data.techniques || []);
      reset({
        date: data.date,
        duration: data.duration,
        sessionType: data.sessionType,
        partner: data.partner,
        notes: data.notes,
        wins: data.wins,
        losses: data.losses
      });
    } else {
      navigate('/sessions');
    }
  }, [id, getSessionById, navigate, reset]);

  const outcomes = ['Attempted', 'Succeeded', 'Failed', 'Taught'];
  const sessionTypes = [
    'Rolling',
    'Drilling',
    'Competition Prep',
    'Fundamentals',
    'Open Mat',
    'Private Lesson',
    'Boxing',
    'MMA'
  ];

  const addTechnique = () => {
    if (newTechnique.name.trim()) {
      setTechniques([...techniques, { ...newTechnique, id: Date.now() }]);
      setNewTechnique({ name: '', outcome: 'Attempted' });
    }
  };

  const removeTechnique = (techId) => {
    setTechniques(techniques.filter(t => t.id !== techId));
  };

  const onSubmit = (data) => {
    const updatedData = {
      ...data,
      techniques,
      duration: parseInt(data.duration),
      wins: parseInt(data.wins) || 0,
      losses: parseInt(data.losses) || 0
    };
    updateSession(id, updatedData);
    navigate('/sessions');
  };

  if (!session) return null;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-3xl mx-auto">
      <div className="mb-6">
        <Link to="/sessions">
          <Button variant="ghost" size="sm" className="mb-4">
            <FiArrowLeft className="w-4 h-4 mr-2" />
            Back to Sessions
          </Button>
        </Link>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Edit Training Session</h1>
      </div>

      <Card>
        <CardContent className="p-6">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Date</Label>
                <Input type="date" {...register('date', { required: true })} />
              </div>
              <div>
                <Label>Duration (min)</Label>
                <Input type="number" {...register('duration', { required: true })} />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Session Type</Label>
                <select 
                  className="w-full h-10 border rounded-md px-3 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500" 
                  {...register('sessionType')}
                >
                  {sessionTypes.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <Label>Partner</Label>
                <Input {...register('partner')} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div><Label>Wins</Label><Input type="number" {...register('wins')} /></div>
              <div><Label>Losses</Label><Input type="number" {...register('losses')} /></div>
            </div>

            <div>
              <Label>Techniques</Label>
              <div className="flex gap-2 mb-4">
                <Input
                  placeholder="Technique name..."
                  value={newTechnique.name}
                  onChange={(e) => setNewTechnique({ ...newTechnique, name: e.target.value })}
                />
                <select
                  className="border rounded-md px-2 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={newTechnique.outcome}
                  onChange={(e) => setNewTechnique({ ...newTechnique, outcome: e.target.value })}
                >
                  {outcomes.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
                <Button type="button" onClick={addTechnique}><FiPlus /></Button>
              </div>
              <div className="space-y-2">
                {techniques.map(t => (
                  <div key={t.id} className="flex justify-between items-center p-2 bg-slate-50 dark:bg-slate-800/50 rounded border border-slate-200 dark:border-slate-700">
                    <span className="text-sm text-slate-900 dark:text-slate-100">{t.name} ({t.outcome})</span>
                    <button type="button" onClick={() => removeTechnique(t.id)} className="text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors"><FiX /></button>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea rows={4} {...register('notes')} />
            </div>

            <div className="flex gap-3">
              <Button type="submit" className="flex-1"><FiSave className="mr-2" /> Save Changes</Button>
              <Button type="button" variant="outline" onClick={() => navigate('/sessions')}>Cancel</Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default SessionEdit;